// src/components/Pages/ResellPipelinePage.js
import React, { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Group,
    Alert,
    Center,
    Table,
    Select,
    Checkbox,
    ActionIcon,
    TextInput,
    Modal,
    Button,
    Switch,
    Tooltip,
    Pagination, // <-- ADDED for pagination
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
    IconReportAnalytics,
    IconAlertCircle,
    IconWorld,
    IconLanguage,
    IconRadar2,
    IconTrash,
    IconCheck,
} from '@tabler/icons-react';
import {
    getFirestore,
    collection,
    query,
    where,
    onSnapshot,
    doc,
    updateDoc,
    deleteDoc,
    writeBatch,
} from 'firebase/firestore';
import { notifications } from '@mantine/notifications';

import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useNavigate } from 'react-router-dom';

// ----------------- utils (hoisted and pure) -----------------
const CMS_EDIT_URL_BASE =
    'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';
const CMS_RESELL_URL_BASE =
    'https://controlcentre.loveincorporated.com/msnglobal/Content/Resell/Create/';

const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date?.toDate === 'function') return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d) ? null : d;
    }
    return null;
};

const toISODateString = (date) => {
    const d = normalizeDate(date);
    return d ? d.toLocaleDateString('sv-SE') : null;
};

const validateAndFormatTime = (timeStr) => {
    if (!timeStr) return { isValid: false, error: 'Time cannot be empty.' };
    const parts = timeStr.split(':');
    if (parts.length === 1 && /^\d{1,2}$/.test(parts[0])) {
        const h = +parts[0];
        if (h >= 0 && h <= 23) return { isValid: true, formattedTime: `${parts[0].padStart(2, '0')}:00` };
        return { isValid: false, error: 'Hour must be 0-23.' };
    }
    if (parts.length === 2) {
        const [hs, ms] = parts;
        if (!/^\d{1,2}$/.test(hs) || !/^\d{1,2}$/.test(ms)) {
            return { isValid: false, error: 'Use only numbers for HH and MM.' };
        }
        const h = +hs, m = +ms;
        if (h < 0 || h > 23) return { isValid: false, error: 'Hour must be 0-23.' };
        if (m < 0 || m > 59) return { isValid: false, error: 'Minute must be 0-59.' };
        return { isValid: true, formattedTime: `${hs.padStart(2, '0')}:${ms.padStart(2, '0')}` };
    }
    return { isValid: false, error: 'Invalid format. Use HH:MM.' };
};

const formatGroupHeaderDate = (date) => {
    const d = normalizeDate(date);
    if (!d) return 'Unscheduled';
    return d.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const calculateDaysResting = (date) => {
    const lastDate = normalizeDate(date);
    if (!lastDate) return 'N/A';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfLastDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
    const diff = startOfToday - startOfLastDate;
    return Math.floor(diff / (1000 * 3600 * 24));
};

// ----------------- Row -----------------
const PipelineRow = React.memo(function PipelineRow({
    item,
    isDark,
    theme,
    usersMap,
    selectedIds,
    savingId,
    completingId,
    handleRowSelect,
    handleFieldUpdate,
    handleTimeSegmentChange,
    setItemToComplete,
    setItemToDelete,
}) {
    const pubDateValue = normalizeDate(item.pubDate);
    const [hours, minutes] = (item.pubTime || ':').split(':');
    const isSelected = selectedIds.includes(item.id);

    const handleSelect = useCallback(() => handleRowSelect(item.id), [item.id, handleRowSelect]);
    const handleComplete = useCallback(() => setItemToComplete(item), [item, setItemToComplete]);
    const handleDelete = useCallback(() => setItemToDelete(item.id), [item.id, setItemToDelete]);

    const handleNewTitleBlur = useCallback(
        (e) => handleFieldUpdate(item.id, 'newTitle', e.currentTarget.value),
        [item.id, handleFieldUpdate]
    );
    const handleTypeChange = useCallback(
        (v) => handleFieldUpdate(item.id, 'type', v),
        [item.id, handleFieldUpdate]
    );
    const handleDateChange = useCallback(
        (d) => handleFieldUpdate(item.id, 'pubDate', d),
        [item.id, handleFieldUpdate]
    );
    const handleHoursBlur = useCallback(
        (e) => handleTimeSegmentChange(item.id, 'hours', e.currentTarget.value),
        [item.id, handleTimeSegmentChange]
    );
    const handleMinutesBlur = useCallback(
        (e) => handleTimeSegmentChange(item.id, 'minutes', e.currentTarget.value),
        [item.id, handleTimeSegmentChange]
    );

    return (
        <Table.Tr
            key={item.id}
            style={{ backgroundColor: isSelected ? (isDark ? theme.colors.dark[5] : theme.colors.gray[1]) : undefined }}
        >
            <Table.Td>
                <Checkbox checked={isSelected} onChange={handleSelect} />
            </Table.Td>
            <Table.Td>{item.latestTitle || 'Untitled'}</Table.Td>
            <Table.Td>
                <TextInput
                    placeholder="Enter new title..."
                    defaultValue={item.newTitle || ''}
                    onBlur={handleNewTitleBlur}
                    disabled={savingId === item.id}
                    size="xs"
                />
            </Table.Td>
            <Table.Td>{item.Brand || 'N/A'}</Table.Td>
            <Table.Td>
                <Select
                    data={['Resell', 'Major Update', 'Minor Update', 'New', 'Archive']}
                    value={item.type || 'Resell'}
                    onChange={handleTypeChange}
                    disabled={savingId === item.id}
                    size="xs"
                />
            </Table.Td>

            <Table.Td ta="center">{calculateDaysResting(item.lastEntryDate)}</Table.Td>
            <Table.Td ta="center" style={{ fontWeight: 500 }}>
                {item.refreshScore ? `${item.refreshScore.toFixed(1)}%` : 'N/A'}
            </Table.Td>
            <Table.Td ta="center">{item.cdsEntries ?? 'N/A'}</Table.Td>

            <Table.Td>
                <DatePickerInput
                    placeholder="Set date"
                    value={pubDateValue || null}
                    onChange={handleDateChange}
                    size="xs"
                    style={{ minWidth: 140 }}
                    disabled={savingId === item.id}
                />
            </Table.Td>
            <Table.Td>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }} tabIndex={-1}>
                    <TextInput
                        placeholder="HH"
                        defaultValue={hours}
                        onBlur={handleHoursBlur}
                        size="xs"
                        maxLength={2}
                        styles={{ input: { width: 45, textAlign: 'center' } }}
                        disabled={savingId === item.id}
                    />
                    <Text tabIndex={-1}>:</Text>
                    <TextInput
                        placeholder="MM"
                        defaultValue={minutes}
                        onBlur={handleMinutesBlur}
                        size="xs"
                        maxLength={2}
                        styles={{ input: { width: 45, textAlign: 'center' } }}
                        disabled={savingId === item.id}
                    />
                </Box>
            </Table.Td>

            <Table.Td style={{ textAlign: 'center' }}>
                {item.originalId}
            </Table.Td>
            <Table.Td ta="center">
                {item.lastFeedId && (
                    <Group justify="center" gap="xs" wrap="nowrap">
                        <Tooltip label={`Edit Last Variant: ${item.lastFeedId}`} withArrow>
                            <Button
                                component="a"
                                href={`${CMS_EDIT_URL_BASE}${item.lastFeedId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="compact-xs"
                                variant="outline"
                                compact
                            >
                                Edit
                            </Button>
                        </Tooltip>
                        <Tooltip label={`Resell Last Variant: ${item.lastFeedId}`} withArrow>
                            <Button
                                component="a"
                                href={`${CMS_RESELL_URL_BASE}${item.lastFeedId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="compact-xs"
                                variant="filled"
                                color="blue"
                                compact
                            >
                                Resell
                            </Button>
                        </Tooltip>
                        <Tooltip label="Mark as done and remove from pipeline" withArrow>
                            <ActionIcon variant="light" color="green" onClick={handleComplete} loading={completingId === item.id}>
                                <IconCheck size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>

                )}

            </Table.Td>
            <Table.Td>{usersMap[item.addedByUid] || '...'}</Table.Td>
            <Table.Td style={{ display: 'flex', justifyContent: 'center' }}>
                <ActionIcon variant="subtle" color="red" tabIndex={-1} onClick={handleDelete}>
                    <IconTrash size={16} />
                </ActionIcon>
            </Table.Td>
        </Table.Tr>
    );
});

// ----------------- Page -----------------
function ResellPipelinePage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const { currentUser } = useAuth();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [region, setRegion] = useState('US');
    const [language, setLanguage] = useState('EN');
    const [brand, setBrand] = useState('All');
    const [selectedIds, setSelectedIds] = useState([]);
    const [savingId, setSavingId] = useState(null);

    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const [bulkDate, setBulkDate] = useState(null);
    const [bulkTime, setBulkTime] = useState('');
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [bulkTimeError, setBulkTimeError] = useState(null);

    const [isGroupedView, setIsGroupedView] = useState(false);
    const [usersMap, setUsersMap] = useState({});
    const [showUnscheduledOnly, setShowUnscheduledOnly] = useState(false);
    const [completingId, setCompletingId] = useState(null);
    const [itemToComplete, setItemToComplete] = useState(null);
    const navigate = useNavigate();

    // ---------- PAGINATION STATE ----------
    const [activePage, setPage] = useState(1);
    const ROWS_PER_PAGE = 50;

    // ---------- data transforms ----------
    const filteredAndSortedItems = useMemo(() => {
        const todayStr = toISODateString(new Date());
        const initial = items.filter((item) => {
            if (showHistory) return true;
            const pubDateStr = toISODateString(item.pubDate);
            return !pubDateStr || pubDateStr >= todayStr;
        });
        const filtered = showUnscheduledOnly ? initial.filter((i) => !i.pubDate) : initial;
        return [...filtered].sort((a, b) => { // Use spread to create a new array to sort safely
            const dateA = normalizeDate(a.pubDate);
            const dateB = normalizeDate(b.pubDate);
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            const d = dateA - dateB;
            if (d !== 0) return d;
            const timeA = a.pubTime || '00:00';
            const timeB = b.pubTime || '00:00';
            return timeA.localeCompare(timeB);
        });
    }, [items, showHistory, showUnscheduledOnly]);

    const deferredItems = useDeferredValue(filteredAndSortedItems);

    const groupedItems = useMemo(() => {
        if (!isGroupedView) return null;
        return deferredItems.reduce((acc, item) => {
            const key = formatGroupHeaderDate(item.pubDate);
            (acc[key] ||= []).push(item);
            return acc;
        }, {});
    }, [deferredItems, isGroupedView]);

    // ---------- PAGINATION LOGIC ----------
    const totalPages = Math.ceil(deferredItems.length / ROWS_PER_PAGE);
    const paginatedItems = useMemo(() => {
        const start = (activePage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return deferredItems.slice(start, end);
    }, [deferredItems, activePage]);

    // Reset page to 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [deferredItems]);


    // ---------- handlers ----------
    const handleArchiveItem = useCallback(
        async (docId) => {
            setSavingId(docId);
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(
                    'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/archiveItem',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ docId }),
                    }
                );
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.message || 'Failed to archive item.');
                }
                notifications.show({
                    title: 'Item Archived',
                    message: 'The article has been removed from the active pipeline.',
                    color: 'blue',
                });
            } catch (e) {
                notifications.show({ title: 'Error', message: e.message, color: 'red' });
            } finally {
                setSavingId(null);
            }
        },
        [currentUser]
    );

    const handleFieldUpdate = useCallback(
        async (id, field, value) => {
            if (field === 'type' && value === 'Archive') {
                handleArchiveItem(id);
                return;
            }
            setItems((cur) => cur.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
            setSavingId(id);
            try {
                const db = getFirestore();
                await updateDoc(doc(db, 'resell_pipeline_items', id), { [field]: value });
            } catch (err) {
                console.error('Error updating document:', err);
                notifications.show({
                    title: 'Save Error',
                    message: 'Your change could not be saved. Please refresh and try again.',
                    color: 'red',
                });
            } finally {
                setSavingId(null);
            }
        },
        [handleArchiveItem]
    );

    const handleRowSelect = useCallback((id) => {
        setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }, []);

    const handleTimeSegmentChange = useCallback(
        (id, segment, value) => {
            setItems((cur) => {
                const it = cur.find((x) => x.id === id);
                const curTime = it?.pubTime || ':';
                let [h, m] = curTime.split(':');
                if (segment === 'hours') h = String(value).padStart(2, '0');
                else m = String(value).padStart(2, '0');
                if (h && !m) m = '00';
                const newTime = `${h || ''}:${m || ''}`;
                handleFieldUpdate(id, 'pubTime', newTime);
                return cur;
            });
        },
        [handleFieldUpdate]
    );

    // ---------- deletion / bulk ops ----------
    const handleRemoveItem = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            const db = getFirestore();
            await deleteDoc(doc(db, 'resell_pipeline_items', itemToDelete));
            notifications.show({
                title: 'Item Removed',
                message: 'The article has been removed from the pipeline.',
                color: 'green',
            });
        } catch (err) {
            console.error('Error removing document:', err);
            notifications.show({
                title: 'Error',
                message: 'Could not remove the item. Please try again.',
                color: 'red',
            });
        } finally {
            setIsDeleting(false);
            setItemToDelete(null);
        }
    };

    const handleBulkUpdate = async (field, value) => {
        if (selectedIds.length === 0 || value === null || value === '') return;
        setItems((cur) => cur.map((it) => (selectedIds.includes(it.id) ? { ...it, [field]: value } : it)));
        setIsBulkUpdating(true);
        try {
            const db = getFirestore();
            const batch = writeBatch(db);
            selectedIds.forEach((id) => batch.update(doc(db, 'resell_pipeline_items', id), { [field]: value }));
            await batch.commit();
            notifications.show({
                title: 'Bulk Update Successful',
                message: `${selectedIds.length} items have been updated.`,
                color: 'green',
            });
            setSelectedIds([]);
            setBulkDate(null);
            setBulkTime('');
        } catch (err) {
            console.error('Bulk update error:', err);
            notifications.show({
                title: 'Bulk Update Error',
                message: 'Could not update all items. Please try again.',
                color: 'red',
            });
        } finally {
            setIsBulkUpdating(false);
        }
    };

    const handleBulkRemove = async () => {
        if (selectedIds.length === 0) return;
        setIsBulkDeleting(true);
        try {
            const db = getFirestore();
            const batch = writeBatch(db);
            selectedIds.forEach((id) => batch.delete(doc(db, 'resell_pipeline_items', id)));
            await batch.commit();
            notifications.show({
                title: 'Bulk Remove Successful',
                message: `${selectedIds.length} items have been removed from the pipeline.`,
                color: 'green',
            });
            setSelectedIds([]);
        } catch (err) {
            console.error('Bulk remove error:', err);
            notifications.show({
                title: 'Bulk Remove Error',
                message: 'Could not remove all items. Please try again.',
                color: 'red',
            });
        } finally {
            setIsBulkDeleting(false);
            setBulkDeleteModalOpen(false);
        }
    };

    const handleMarkAsComplete = async () => {
        if (!itemToComplete) return;
        setCompletingId(itemToComplete.id);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/markItemAsComplete',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ docId: itemToComplete.id }),
                }
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || 'Failed to mark item as complete.');
            }
            notifications.show({
                title: 'Item Completed',
                message: 'The article has been moved to your completed list.',
                color: 'green',
            });
        } catch (e) {
            notifications.show({ title: 'Error', message: e.message, color: 'red' });
        } finally {
            setCompletingId(null);
            setItemToComplete(null);
        }
    };

    // ---------- subscriptions ----------
    useEffect(() => {
        if (!currentUser) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const db = getFirestore();
        const itemsCollection = collection(db, 'resell_pipeline_items');
        const constraints = [where('region', '==', region), where('language', '==', language)];
        if (brand !== 'All') constraints.push(where('Brand', '==', brand));
        const qy = query(itemsCollection, ...constraints);
        const unsub = onSnapshot(
            qy,
            (snap) => {
                const arr = [];
                snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
                setItems(arr);
                setIsLoading(false);
            },
            (err) => {
                console.error('Error fetching pipeline items:', err);
                setError('Failed to fetch pipeline items. Check the developer console for a Firestore index link.');
                setIsLoading(false);
            }
        );
        return () => unsub();
    }, [currentUser, region, language, brand]);

    useEffect(() => {
        const db = getFirestore();
        const usersCollection = collection(db, 'users');
        const unsub = onSnapshot(usersCollection, (snapshot) => {
            const map = {};
            snapshot.forEach((d) => {
                const u = d.data();
                map[d.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown User';
            });
            setUsersMap(map);
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (showUnscheduledOnly) setShowHistory(false);
    }, [showUnscheduledOnly]);

    // ---------- memo for stable props ----------
    const commonRowProps = useMemo(
        () => ({
            isDark,
            theme,
            usersMap,
            selectedIds,
            savingId,
            completingId,
            handleRowSelect,
            handleFieldUpdate,
            handleTimeSegmentChange,
            setItemToComplete,
            setItemToDelete,
        }),
        [
            isDark,
            theme,
            usersMap,
            selectedIds,
            savingId,
            completingId,
            handleRowSelect,
            handleFieldUpdate,
            handleTimeSegmentChange,
        ]
    );

    // ---------- content ----------
    const renderContent = () => {
        if (isLoading) {
            return (
                <Center mt="xl" p="xl">
                    <Loader />
                    <Text ml="md">Loading Pipeline...</Text>
                </Center>
            );
        }
        if (error) {
            return (
                <Alert icon={<IconAlertCircle size="1rem" />} title="Error!" color="red" mt="xl">
                    {error}
                </Alert>
            );
        }
        if (paginatedItems.length === 0) { // Check paginated items
            return (
                <Center mt="xl" p="xl">
                    <Text c="dimmed">No items found for the selected filters.</Text>
                </Center>
            );
        }

        const allOnPageSelected = paginatedItems.length > 0 && paginatedItems.every(i => selectedIds.includes(i.id));
        const someOnPageSelected = paginatedItems.some(i => selectedIds.includes(i.id)) && !allOnPageSelected;

        const handleSelectAll = () => {
            const pageIds = paginatedItems.map(i => i.id);
            if (allOnPageSelected) {
                setSelectedIds(current => current.filter(id => !pageIds.includes(id)));
            } else {
                setSelectedIds(current => [...new Set([...current, ...pageIds])]);
            }
        };

        // Determine which items to render based on view type
        const itemsToRender = isGroupedView ? groupedItems : paginatedItems;

        return (
            <>
                <Table.ScrollContainer minWidth={1800}>
                    <Table
                        striped
                        highlightOnHover
                        verticalSpacing="sm"
                        layout="fixed"
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{ width: 40, position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>
                                    <Checkbox
                                        checked={allOnPageSelected}
                                        indeterminate={someOnPageSelected}
                                        onChange={handleSelectAll}
                                    />
                                </Table.Th>
                                <Table.Th style={{ width: 250, position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>Article Title (Last Used)</Table.Th>
                                <Table.Th style={{ width: 250, position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>New Title</Table.Th>
                                <Table.Th style={{ width: 150 }}>Brand</Table.Th>
                                <Table.Th style={{ width: 150 }}>Type</Table.Th>

                                <Table.Th style={{ width: 75, textAlign: 'center' }}>Days Resting</Table.Th>
                                <Table.Th style={{ width: 75, textAlign: 'center' }}>Score</Table.Th>
                                <Table.Th style={{ width: 75, textAlign: 'center' }}>Variants</Table.Th>

                                <Table.Th style={{ width: 150, textAlign: 'center' }}>Send Date</Table.Th>
                                <Table.Th style={{ width: 130, textAlign: 'center' }}>Send Time</Table.Th>

                                <Table.Th style={{ width: 70, textAlign: 'center' }}>Original ID</Table.Th>
                                <Table.Th style={{ width: 180, textAlign: 'center' }}>Last Variant</Table.Th>
                                <Table.Th style={{ width: 150 }}>Added By</Table.Th>
                                <Table.Th style={{ width: 80, textAlign: 'center' }}>Actions</Table.Th>

                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {isGroupedView
                                ? Object.keys(itemsToRender).map((dateKey) => (
                                    <React.Fragment key={dateKey}>
                                        <Table.Tr
                                            style={{
                                                backgroundColor: isDark ? theme.colors.blue[9] : theme.colors.blue[2],
                                                color: isDark ? theme.colors.blue[1] : theme.colors.blue[8],
                                                position: 'sticky', top: 38, zIndex: 1 // Sticky header height
                                            }}
                                        >
                                            <Table.Td colSpan={14} fw={700}>
                                                {dateKey}
                                            </Table.Td>
                                        </Table.Tr>
                                        {itemsToRender[dateKey].map((item) => (
                                            <PipelineRow key={item.id} item={item} {...commonRowProps} />
                                        ))}
                                    </React.Fragment>
                                ))
                                : itemsToRender.map((item) => (
                                    <PipelineRow key={item.id} item={item} {...commonRowProps} />
                                ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>

                {totalPages > 1 && !isGroupedView && (
                    <Group justify="center" mt="xl">
                        <Pagination total={totalPages} value={activePage} onChange={setPage} />
                    </Group>
                )}
            </>
        );
    };

    // ---------- layout ----------
    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                // KEY STABILITY FIX: Use a non-fixed background to avoid Chrome resize jank
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'scroll', // Use 'scroll' instead of 'fixed'
            }}
        >
            <Container size="xl" py="xl">
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconReportAnalytics
                        color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]}
                        style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }}
                    />
                    Resell Pipeline
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Manage articles selected for republication.
                </Text>

                <Paper withBorder shadow="sm" p="md" radius="md">
                    <Group grow>
                        <Select
                            label="Region"
                            leftSection={<IconWorld size={16} />}
                            data={[
                                'UK', 'US', '--', 'AE', 'AR', 'AT', 'AU', 'BE', 'CA', 'CH', 'CL', 'CO', 'DE',
                                'ES', 'FR', 'IE', 'IN', 'MX', 'MY', 'NZ', 'PE', 'PH', 'SG', 'XL', 'ZA',
                            ]}
                            value={region}
                            onChange={(v) => setRegion(v || '')}
                        />
                        <Select
                            label="Language"
                            leftSection={<IconLanguage size={16} />}
                            data={['EN', 'DE', 'ES', 'FR']}
                            value={language}
                            onChange={(v) => setLanguage(v || '')}
                        />
                        <Select
                            label="Brand"
                            leftSection={<IconRadar2 size={16} />}
                            data={['All', 'loveMONEY', 'loveFOOD', 'loveEXPLORING', 'lovePROPERTY']}
                            value={brand}
                            onChange={(v) => setBrand(v || 'All')}
                        />
                    </Group>
                    <Group justify="space-between" mt="md" align="flex-end">
                        <Group>
                            <Switch
                                label="Show Past Items"
                                checked={showHistory}
                                onChange={(e) => setShowHistory(e.currentTarget.checked)}
                                disabled={showUnscheduledOnly}
                            />
                            <Switch
                                label="Group by Date"
                                checked={isGroupedView}
                                onChange={(e) => setIsGroupedView(e.currentTarget.checked)}
                            />
                            <Switch
                                label="Unscheduled Only"
                                checked={showUnscheduledOnly}
                                onChange={(e) => setShowUnscheduledOnly(e.currentTarget.checked)}
                            />
                        </Group>
                        <Button variant="outline" onClick={() => navigate('/resell-radar')}>
                            Go to Resell Radar
                        </Button>
                    </Group>
                </Paper>
            </Container>

            {selectedIds.length > 0 && (
                <Container size="xl" pb="md">
                    <Paper withBorder shadow="sm" p="md" radius="md">
                        <Group align="flex-end" justify="space-between">
                            <Text fw={500}>{selectedIds.length} item(s) selected</Text>
                            <Group>
                                <DatePickerInput
                                    placeholder="Set date for selected"
                                    value={bulkDate}
                                    onChange={setBulkDate}
                                    size="xs"
                                    clearable
                                />
                                <Button
                                    size="xs"
                                    onClick={() => handleBulkUpdate('pubDate', bulkDate)}
                                    disabled={!bulkDate || isBulkUpdating || isBulkDeleting}
                                    loading={isBulkUpdating}
                                >
                                    Set Date
                                </Button>
                                <TextInput
                                    placeholder="HH:MM"
                                    value={bulkTime}
                                    onChange={(e) => {
                                        const t = e.currentTarget.value;
                                        setBulkTime(t);
                                        const v = validateAndFormatTime(t);
                                        setBulkTimeError(v.isValid ? null : v.error);
                                    }}
                                    size="xs"
                                    error={bulkTimeError}
                                    styles={{ input: { width: 80, textAlign: 'center' } }}
                                />
                                <Button
                                    size="xs"
                                    onClick={() => {
                                        const v = validateAndFormatTime(bulkTime);
                                        if (v.isValid) handleBulkUpdate('pubTime', v.formattedTime);
                                        else setBulkTimeError(v.error);
                                    }}
                                    disabled={!bulkTime || isBulkUpdating || isBulkDeleting || !!bulkTimeError}
                                    loading={isBulkUpdating}
                                >
                                    Set Time
                                </Button>
                                <Button size="xs" variant="subtle" color="gray" title="Coming soon!">
                                    Auto-gen Titles (soon)
                                </Button>
                                <Button size="xs" variant="subtle" color="gray" title="Coming soon!">
                                    Multi-region (soon)
                                </Button>
                                <Button size="xs" variant="subtle" color="gray" title="Coming soon!">
                                    Send to CMS (tbc)
                                </Button>
                                <Button
                                    size="xs"
                                    color="red"
                                    variant="outline"
                                    onClick={() => setBulkDeleteModalOpen(true)}
                                    disabled={isBulkUpdating || isBulkDeleting}
                                >
                                    Remove Selected
                                </Button>
                            </Group>
                        </Group>
                    </Paper>
                </Container>
            )}

            <Container fluid px="xl" pb="xl">
                <Paper withBorder shadow="sm" radius="md" p="md">
                    {/* Note: Switched to p="md" for padding */}
                    {renderContent()}
                </Paper>
            </Container>

            {/* Modals */}
            <Modal opened={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Confirm Removal" centered size="sm">
                <Text size="sm">Are you sure you want to remove this article from the pipeline?</Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setItemToDelete(null)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={handleRemoveItem} loading={isDeleting}>
                        Remove Item
                    </Button>
                </Group>
            </Modal>

            <Modal
                opened={isBulkDeleteModalOpen}
                onClose={() => setBulkDeleteModalOpen(false)}
                title="Confirm Bulk Removal"
                centered
                size="sm"
            >
                <Text size="sm">
                    Are you sure you want to remove the {selectedIds.length} selected articles from the pipeline? This action
                    cannot be undone.
                </Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setBulkDeleteModalOpen(false)} disabled={isBulkDeleting}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={handleBulkRemove} loading={isBulkDeleting}>
                        Remove Items
                    </Button>
                </Group>
            </Modal>

            <Modal opened={!!itemToComplete} onClose={() => setItemToComplete(null)} title="Confirm Completion" centered size="md">
                <Text fw={700} my="md" style={{ fontStyle: 'italic' }}>
                    {itemToComplete?.latestTitle}
                </Text>
                <Text size="md">
                    Please confirm that you have scheduled the next incarnation of this in the CMS before removing it from the
                    pipeline...
                </Text>
                <Group justify="flex-end" mt="xl">
                    <Button variant="default" onClick={() => setItemToComplete(null)} disabled={!!completingId}>
                        Cancel
                    </Button>
                    <Button color="green" onClick={handleMarkAsComplete} loading={!!completingId}>
                        Complete
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
}

export default ResellPipelinePage;
